import { FormikProvider, useFormik } from 'formik';
import React, { useLayoutEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import ResponsiveDialog from '../ResponsiveDialog';
import { translate } from '../../helpers/i18n';
import useBoundCallback from '../../helpers/Hooks/useBoundCallback';
import { v4 as uuid } from 'uuid';

/**
 * @typedef {Object} FormikDialogProps
 * @property {boolean} [allowPristineSubmit=false] When false (default), the confirm button is disabled until the form is dirty. Set to true to allow confirmation with initial values.
 * @property {string} [cancelText='components.ValidationDialog.cancelText'] The text or localized string that appears in the Cancel button. Defaults to 'components.ValidationDialog.cancelText'.
 * @property {string} [confirmText='components.ValidationDialog.confirmText'] The text or localized string that appears in the Confirm button. Defaults to 'components.ValidationDialog.confirmText'.
 * @property {string} [cancelTrackEvent] The name of the analytics event to dispatch when Dialog is cancelled
 * @property {string} [confirmTrackEvent] The name of the analytics event to dispatch when Dialog is confirmed
 * @property {boolean} [closeOnConfirm] For multistep forms, disables the close when passing in a function to onSubmit. Defaults to True.
 * @property {boolean} [disabled] Disables the ability to confirm a dialog
 * @property {string} [disclaimer] A string, or localized string path, that displays after the form, but before the error and action buttons.
 * @property {string} [error] Error provided to the dialog to display. This is helpful if there is external validation logic that can provide an error.
 * @property {React.RefObject<ReturnType<import('formik')['useFormik']>>} [formikRef] Formik ref to be used to access the FormikBag context outside of the component
 * @property {string} [formId] An id to attach to the Confirm button that will associate it to a given form field. If you do not provide it, one will be created.
 * @property {any} [initialValues] The initialValues of the form provided to the `useFormik` hook.
 * @property {string} [loadingText] The optional text to display in the Confirm button when a submission is in progress
 * @property {Function} [onCancel] The callback executed when the Cancel button is clicked.
 * @property {Function} onClose The callback executed when the Dialog is closed by either clicking outside the Dialog area, or by clicking the (X) in the corner. This callback is also used for the Confirm button when `onConfirm` is not provided.
 * @property {() => (Promise<any>|void)} [onConfirm] The callback executed when the Confirm button is clicked.
 * @property {boolean} [open] The controlling property for the open/close of the dialog
 * @property {boolean} [sectioned] Opt in to the standard sectioned dialog styling (white header and footer separated by dividers from a grey body).
 * @property {string} [title] The title that appears in the Dialog header
 * @property {Record<string,any>} [translationOptions] The translation options provided to all localized strings in the Dialog
 * @property {import('formik').FormikConfig['validate']} validate The validate function provided to `useFormik`
 * @property {boolean} [validateOnBlur] The validateOnBlur provided to `useFormik`
 * @property {boolean} [validateOnChange] The validateOnChange provided to `useFormik`
 * @property {boolean} [validateOnMount] The validateOnMount provided to `useFormik`
 * @property {(import('yup').AnyObjectSchema|(values: import('formik').FormikValues) => import('yup').AnyObjectSchema)} [validationSchema] The validationSchema provided to `useFormik`.
 */

/**
 * A dialog that wraps it's children in a FormikProvider, and handles the validation and submission
 *
 * @param {import('react').PropsWithChildren<FormikDialogProps & import('@mui/material').DialogProps>} props
 * @return {import('prop-types').ReactComponentLike}
 */
const FormikDialog = React.forwardRef((props, ref) => {
    const {
        allowPristineSubmit = false,
        cancelText = 'components.ValidationDialog.cancelText',
        confirmText = 'components.ValidationDialog.confirmText',
        cancelTrackEvent,
        children,
        closeOnConfirm = true,
        confirmTrackEvent,
        container,
        disabled,
        disclaimer,
        enableReinitialize,
        error: passedError,
        formId: passedFormId,
        formikRef,
        loadingButtonIndicator = true,
        initialTouched,
        initialValues,
        loading: passedLoading,
        loadingText,
        maxWidth,
        onBack,
        onCancel,
        onClose,
        onConfirm,
        open,
        showCloseButton,
        title,
        translationOptions,
        validate,
        validateOnBlur,
        validateOnChange,
        validateOnMount,
        validationSchema,
        ...remain
    } = props;

    const mounted = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(passedError);
    const formId = useRef(passedFormId || uuid());

    const handleFormikValidate = useBoundCallback(
        (providedValidate, values) => {
            const errors = (providedValidate && providedValidate(values)) || undefined;
            return errors;
        },
        [validate]
    );

    const handleFormikSubmit = useBoundCallback(
        (closeOnConfirm, handleSubmit, values) => {
            setError('');
            setLoading(true);
            // We need to get a closure reference to avoid race conditions
            const shouldClose = closeOnConfirm;
            return ((handleSubmit && handleSubmit(values)) || Promise.resolve(values))
                .then(response => {
                    if (mounted.current) {
                        setLoading(false);
                    }
                    if (shouldClose && onClose) {
                        onClose(response);
                    }
                    return response;
                })
                .catch(error => {
                    const { message = error } = error;
                    if (mounted.current) {
                        setError(message);
                        setLoading(false);
                    }
                    throw error;
                });
        },
        [closeOnConfirm, onConfirm || onClose]
    );

    const formikBag = useFormik({
        validate: handleFormikValidate,
        initialTouched: initialTouched || {},
        initialValues: initialValues || {},
        onSubmit: handleFormikSubmit,
        validateOnMount,
        validateOnBlur,
        validateOnChange,
        validationSchema,
        enableReinitialize,
    });
    formikRef && (formikRef.current = formikBag);

    const handleConfirm = useBoundCallback(
        (submit, event) => {
            submit(event);
        },
        [formikBag.handleSubmit]
    );

    useLayoutEffect(() => {
        // Initial Values may have properties that do not have form fields, so we need to set them manually in this case on load.
        initialValues && formikBag.setValues(initialValues, enableReinitialize);
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useLayoutEffect(() => {
        if (!open && mounted.current) {
            formikBag.handleReset();
        }
        if (open) {
            setError(passedError);
            formikBag?.validateForm();
        }
    }, [open]);

    useLayoutEffect(() => {
        setError(passedError);
    }, [passedError]);

    return (
        <FormikProvider value={formikBag}>
            <ResponsiveDialog
                ref={ref}
                title={title}
                disclaimer={disclaimer}
                open={loading || !!open}
                isConfirmDisabled={
                    disabled ||
                    loading ||
                    !formikBag.isValid ||
                    (!allowPristineSubmit && !formikBag.dirty)
                }
                isLoading={passedLoading || loading}
                loadingText={loadingText}
                loadingButtonIndicator={loadingButtonIndicator}
                maxWidth={maxWidth}
                onClose={onClose}
                onCancel={onCancel}
                onConfirm={onConfirm && handleConfirm}
                cancelText={cancelText && translate(cancelText)}
                confirmText={typeof confirmText === 'string' ? translate(confirmText) : confirmText}
                cancelTrackEvent={cancelTrackEvent}
                confirmTrackEvent={confirmTrackEvent}
                formId={formId.current}
                translationOptions={translationOptions}
                error={error}
                showCloseButton={showCloseButton}
                onBack={onBack}
                container={container}
                {...remain}
            >
                <form id={formId.current} onReset={formikBag.handleReset} onSubmit={handleConfirm}>
                    {typeof children === 'function' ? children(formikBag) : children}
                </form>
            </ResponsiveDialog>
        </FormikProvider>
    );
});

FormikDialog.displayName = 'FormikDialog';

FormikDialog.propTypes = {
    allowPristineSubmit: PropTypes.bool,
    cancelText: PropTypes.string,
    cancelTrackEvent: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    closeOnConfirm: PropTypes.bool,
    confirmText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    confirmTrackEvent: PropTypes.string,
    container: PropTypes.object,
    disabled: PropTypes.bool,
    disclaimer: PropTypes.string,
    enableReinitialize: PropTypes.bool,
    error: PropTypes.string,
    formikRef: PropTypes.object,
    formId: PropTypes.string,
    loadingButtonIndicator: PropTypes.bool,
    initialTouched: PropTypes.object,
    initialValues: PropTypes.object,
    loading: PropTypes.bool,
    loadingText: PropTypes.string,
    maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    onClose: PropTypes.func.isRequired,
    onBack: PropTypes.func,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    open: PropTypes.bool,
    sectioned: PropTypes.bool,
    showCloseButton: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    translationOptions: PropTypes.object,
    validate: PropTypes.func,
    validateOnMount: PropTypes.bool,
    validateOnBlur: PropTypes.bool,
    validateOnChange: PropTypes.bool,
    validationSchema: PropTypes.any,
};

export default FormikDialog;
